import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { HeatBand } from '@/modules/heat/types';

interface SignalPillProps {
  /** Heat band drives intensity — never expose raw scores. */
  band?: HeatBand;
  leading?: ReactNode;
  children: ReactNode;
  className?: string;
}

const BAND: Record<HeatBand, string> = {
  cool: 'border-border-subtle text-text-secondary bg-white/[0.03]',
  warm: 'border-border-pink text-text-primary bg-pink/[0.06]',
  hot: 'border-pink/40 text-pink bg-pink/[0.08]',
  pulse: 'border-pink text-pink bg-pink/[0.12] shadow-signal',
};

/**
 * Compact pill used for mood, distance, room labels, and pulse indicators.
 * Defaults to a calm cool band — only escalate visually for genuine signal.
 */
export const SignalPill = ({ band = 'cool', leading, children, className }: SignalPillProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-sharp border px-2 py-1',
      'font-display text-[10px] font-semibold uppercase tracking-widest',
      BAND[band],
      className,
    )}
  >
    {leading}
    {children}
  </span>
);
